import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import StatusPill, { STATUS_HELP } from "../components/StatusPill";
import { HelpTip } from "../components/HelpTip";
import PaymentPill from "../components/PaymentPill";
import { formatCurrency, formatDate } from "../lib/format";
import { Search, Filter, Download } from "lucide-react";

const SYNC_OPTIONS = [
  { value: "all", label: "all" },
  { value: "held", label: "held (all)" },
  { value: "synced", label: "synced" },
  { value: "pending", label: "pending" },
  { value: "failed", label: "failed" },
  { value: "needs_mapping", label: "needs mapping" },
  { value: "awaiting_sender_approval", label: "awaiting vendor approval" },
  { value: "sender_blocked", label: "vendor blocked" },
  { value: "no_match", label: "no matching company" },
  { value: "held_billing", label: "held — billing" },
];
const ERP_FALLBACK = ["all", "quickbooks", "xero", "zoho", "bc", "freshbooks", "odoo", "netsuite"];
const STATUS_OPTIONS = ["all", "paid", "pending", "overdue", "draft"];

const SYNC_HELP = (
  <ul className="space-y-1">
    <li><strong>Held:</strong> {STATUS_HELP.held}</li>
    <li><strong>Ready / pending:</strong> {STATUS_HELP.ready}</li>
    <li><strong>Synced:</strong> {STATUS_HELP.synced}</li>
    <li><strong>Failed:</strong> {STATUS_HELP.failed}</li>
    <li><strong>Held (all):</strong> Held invoices that are waiting on you.</li>
  </ul>
);

export default function Invoices() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [syncStatus, setSyncStatus] = useState(searchParams.get("sync_status") || "all");
  const [sourceErp, setSourceErp] = useState(searchParams.get("source_erp") || "all");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [companyId, setCompanyId] = useState(searchParams.get("company_id") || "all");
  const [customer, setCustomer] = useState(searchParams.get("customer") || "all");
  const [vendor, setVendor] = useState(searchParams.get("vendor") || "all");
  const [facets, setFacets] = useState({ companies: [], customers: [], vendors: [], source_erps: [] });
  const erpOptions = facets.source_erps && facets.source_erps.length
    ? ["all", ...facets.source_erps]
    : ERP_FALLBACK;

  // Keep the URL in sync with the current filters so the deep-link stays live
  // (Customer list "stuck" links, bookmarking, refresh, etc.).
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set("search", search);
    if (syncStatus && syncStatus !== "all") next.set("sync_status", syncStatus);
    if (sourceErp && sourceErp !== "all") next.set("source_erp", sourceErp);
    if (status && status !== "all") next.set("status", status);
    if (companyId && companyId !== "all") next.set("company_id", companyId);
    if (customer && customer !== "all") next.set("customer", customer);
    if (vendor && vendor !== "all") next.set("vendor", vendor);
    setSearchParams(next, { replace: true });
  }, [search, syncStatus, sourceErp, status, companyId, customer, vendor, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/invoices", {
      params: { search: search || undefined, sync_status: syncStatus,
                source_erp: sourceErp, status,
                company_id: companyId, customer, vendor },
    });
    setInvoices(data);
    setLoading(false);
  }, [search, syncStatus, sourceErp, status, companyId, customer, vendor]);

  useEffect(() => {
    api.get("/invoices/filters").then(({ data }) => setFacets(data || {}));
  }, []);

  const [glConnected, setGlConnected] = useState(true);
  useEffect(() => {
    api.get("/onboarding/checklist")
      .then(({ data }) => setGlConnected(!!data.steps.find((s) => s.key === "connect_gl")?.done))
      .catch(() => setGlConnected(true));
  }, []);

  const filtersActive = !!search || [syncStatus, sourceErp, status, companyId, customer, vendor]
    .some((v) => v && v !== "all");
  const clearFilters = () => {
    setSearch(""); setSyncStatus("all"); setSourceErp("all"); setStatus("all");
    setCompanyId("all"); setCustomer("all"); setVendor("all");
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = { search: search || undefined, sync_status: syncStatus,
                       source_erp: sourceErp, status,
                       company_id: companyId, customer, vendor };
      const res = await api.get("/invoices/export.csv", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const now = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      a.download = `lingua-franca-invoices-${now}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const truncated = res.headers["x-export-truncated"] === "true";
      if (truncated) {
        alert(`Export capped at 5,000 rows. Tighten the filters if you need more.`);
      }
    } catch (e) {
      alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="ledger"
        title="Invoices"
        description="All invoices captured from your vendors' GLs. Click any row to see fields, per-line GL mapping, and sync history."
        testid="invoices-header"
        help="invoice-statuses"
        actions={
          <button
            type="button"
            className="btn-outline flex items-center gap-2"
            onClick={exportCsv}
            disabled={exporting}
            data-testid="export-invoices-csv"
          >
            <Download size={12} /> {exporting ? "exporting…" : "export csv"}
          </button>
        }
      />

      <div className="p-8 space-y-6">
        {/* Filters */}
        <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="invoice-filters">
          <div className="md:col-span-2 relative self-end">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="search"
              className="input-field pl-9"
              placeholder="search invoice # / customer / email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="invoice-search-input"
            />
          </div>
          <FilterSelect label="sync" value={syncStatus} onChange={setSyncStatus} options={SYNC_OPTIONS} testid="filter-sync-status"
                        help={<HelpTip text={SYNC_HELP} anchor="invoice-statuses" label="About sync statuses" testid="help-sync-filter" />} />
          <FilterSelect label="source erp" value={sourceErp} onChange={setSourceErp} options={erpOptions} testid="filter-source-erp" />
          <FilterSelect label="status" value={status} onChange={setStatus} options={STATUS_OPTIONS} testid="filter-status" />
          {facets.companies.length > 1 && (
            <FacetSelect label="company" value={companyId} onChange={setCompanyId}
                         options={[{ id: "all", name: "all" }, ...facets.companies]}
                         testid="filter-company" />
          )}
          <FacetSelect label="customer"
                       value={customer} onChange={setCustomer}
                       options={[{ id: "all", name: "all" },
                                 ...(customer !== "all" && !facets.customers.includes(customer)
                                     ? [{ id: customer, name: customer }] : []),
                                 ...facets.customers.map((c) => ({ id: c, name: c }))]}
                       testid="filter-customer" />
          <FacetSelect label="vendor"
                       value={vendor} onChange={setVendor}
                       options={[{ id: "all", name: "all" }, ...facets.vendors]}
                       testid="filter-vendor" />
        </div>

        {/* Table */}
        <div className="card-flat overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-900 flex items-center justify-between">
            <div className="label-mini" data-testid="invoice-count">
              {loading ? "loading…" : `${invoices.length} record${invoices.length === 1 ? "" : "s"}`}
            </div>
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Filter size={12} /> filters applied
            </div>
          </div>
          <table className="data-table" data-testid="invoices-table">
            <thead>
              <tr>
                <th>invoice #</th>
                <th>vendor</th>
                <th>source</th>
                <th>customer</th>
                <th>issued</th>
                <th>due</th>
                <th className="text-right">amount</th>
                <th>
                  payment
                  <HelpTip text="Read from the receiver's accounting system. LF can't change it."
                           anchor="invoice-statuses" label="About the Payment column" testid="help-payment-column" />
                </th>
                <th>
                  status
                  <HelpTip text={SYNC_HELP} anchor="invoice-statuses" label="About statuses" testid="help-status-column" />
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && invoices.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-12 text-center" data-testid="invoices-empty"
                      data-empty-kind={filtersActive ? "filtered" : glConnected ? "no-invoices" : "no-gl"}>
                    <EmptyState filtersActive={filtersActive} glConnected={glConnected}
                                onClear={clearFilters} />
                  </td>
                </tr>
              )}
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                  data-testid={`invoice-row-${inv.invoice_number}`}
                >
                  <td className="font-mono">{inv.invoice_number}</td>
                  <td>
                    <div className="text-neutral-900" data-testid={`vendor-${inv.invoice_number}`}>
                      {inv.sender_company_name || "—"}
                    </div>
                  </td>
                  <td>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-600 px-1.5 py-0.5 border border-neutral-200"
                          data-testid={`source-erp-${inv.invoice_number}`}>
                      {inv.source_erp || "—"}
                    </span>
                  </td>
                  <td>
                    <div className="text-neutral-900">{inv.customer_name}</div>
                    <div className="text-xs text-neutral-500">{inv.customer_email}</div>
                  </td>
                  <td className="font-mono text-xs">{formatDate(inv.issue_date)}</td>
                  <td className="font-mono text-xs">{formatDate(inv.due_date)}</td>
                  <td className="text-right font-mono">{formatCurrency(inv.total_amount, inv.currency)}</td>
                  <td><PaymentPill invoice={inv} /></td>
                  <td><StatusPill invoice={inv} withHelp /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function EmptyState({ filtersActive, glConnected, onClear }) {
  const [text, cta] = filtersActive
    ? ["No invoices match these filters.",
       <button type="button" className="btn-outline" onClick={onClear} data-testid="invoices-clear-filters">Clear filters</button>]
    : !glConnected
      ? ["Connect your accounting system to start receiving and sending invoices.",
         <Link to="/connections" className="btn-primary inline-block" data-testid="invoices-empty-cta">Go to Connections</Link>]
      : ["No invoices yet. New invoices are picked up on a regular schedule.",
         <Link to="/help#quick-start" className="btn-outline inline-block" data-testid="invoices-empty-cta">Learn how it works</Link>];
  return (
    <>
      <div className="text-sm text-neutral-700" data-testid="invoices-empty-text">{text}</div>
      <div className="mt-4">{cta}</div>
    </>
  );
}

function FilterSelect({ label, value, onChange, options, testid, help }) {
  return (
    <div>
      <label className="label-mini flex items-center mb-1">{label}{help}</label>
      <select
        className="input-field"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          const l = typeof o === "string" ? o : o.label;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </div>
  );
}

function FacetSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="label-mini block mb-1">{label}</label>
      <select
        className="input-field"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}
